import { Flex, Form, Input, Typography } from "antd";
import queryClient from "api/queryClient";
import { useStoreCommissionGroupMutation } from "api/TenantSlug/CommissionGroup/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { TOAST_TYPES } from "localConstants";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { showCustomToast } from "utils/toast";

import { CommissionGroupData, schema } from "./formSchema";

interface AddCommissionGroupFormProps {
    onSubmit?: () => void;
    formId?: string;
}

const AddCommissionGroupFormContent: FC<AddCommissionGroupFormProps> = () => {
    const { t } = usePaldockTranslation("forms.add-commission-group");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const generalFormUtils = useGeneralForm<CommissionGroupData>();
    const { control, fieldNames } = generalFormUtils;

    return (
        <div className={"bg-gray-100 rounded-lg border-dashed p-1 "}>
            <Form layout="vertical">
                <Flex vertical className="p-4 bg-white rounded-lg m-3">
                    <Typography.Title level={4}>{t("sections.commission_group")}</Typography.Title>
                    <FormItem control={control} name={fieldNames.title.name} label={tFields("title.label")}>
                        <Input placeholder={tFields("title.placeholder")} />
                    </FormItem>
                    <FormItem control={control} name={fieldNames.description?.name || "description"} label={tFields("description.label")}>
                        <Input placeholder={tFields("description.placeholder")} />
                    </FormItem>
                </Flex>
            </Form>
        </div>
    );
};

export const AddCommissionGroupForm: FC<AddCommissionGroupFormProps> = ({ formId, onSubmit }) => {
    const { t } = usePaldockTranslation("forms.add-commission-group");
    const { t: tCommon } = usePaldockTranslation("common");

    const defaultValues = {
        title: "",
        description: "",
    };

    const storeCommissionGroupMutation = useStoreCommissionGroupMutation();
    const generalFormUtils = useGeneralForm<CommissionGroupData>();
    const userUtils = useUser();
    // const tenantSlug = userUtils.getActiveTenant()?.slug_name;
    // const navigate = useNavigate();

    const onSubmitForm = (data: CommissionGroupData) => {
        const transformedData = {
            ...data,
            description: data.description ?? null
        }
        storeCommissionGroupMutation.mutate(transformedData, {
            onSuccess: (response) => {
                if (response && response.commissionGroup) {
                    queryClient.invalidateQueries({ queryKey: ["getCommissionGroups"] });
                    queryClient.invalidateQueries({ queryKey: ["getCommissionGroup"] });
                    queryClient.invalidateQueries({ queryKey: ["getCommissions"] });
                    queryClient.invalidateQueries({ queryKey: ["getRequests"] });
                    showCustomToast(
                        {
                            title: t("messages.add_success"),
                            buttons: [
                                {
                                    label: tCommon("open"),
                                    href: response?.commissionGroup?.id
                                        ? `/${userUtils.getActiveTenant()?.slug_name}/affiliate/commission/${response.commissionGroup.id}`
                                        : undefined,
                                },
                            ],
                        },
                        TOAST_TYPES.SUCCESS
                    );
                    onSubmit?.();

                    // navigate({
                    //     to: "/$tenantSlug/affiliate/commission/$commissionGroupId",
                    //     params: {
                    //         commissionGroupId: response.commissionGroup.id,
                    //         tenantSlug: tenantSlug || "",
                    //     },
                    // });
                }
            },
            onError: (error) => {
                console.error(`${t("messages.add_error")}: ${error.message}`);
                generalFormUtils.handleErrorsFromErrorResponse(error);
            },
        });
    };

    return (
        <GeneralFormProvider<CommissionGroupData>
            id={formId}
            schema={schema}
            defaultValues={defaultValues}
            onSubmit={onSubmitForm}
        >
            <AddCommissionGroupFormContent />
        </GeneralFormProvider>
    );
};
